import { useMemo, useState } from 'react'
import { useCurrentUser } from '~/lib/auth/use-current-user'
import type { AdminListUser, AdminUpdatePayload } from '~/lib/api/types'
import { ApiHttpError } from '~/lib/api/client'
import { useAdmins, useSetAdmin } from './use-admins'
import styles from './admins-page.module.scss'

export function AdminsPage() {
  const { data: me } = useCurrentUser()
  const isAdmin = me?.user.is_admin ?? false
  const meId = me?.user.id ?? null

  const { data, isLoading, isError, refetch } = useAdmins(isAdmin)

  if (!isAdmin) {
    return (
      <div className={styles.page}>
        <h1 className={styles.heading}>Admins</h1>
        <p className={styles.placeholder}>This page is admin-only.</p>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <header>
        <h1 className={styles.heading}>Admins</h1>
        <p className={styles.subhead}>
          grant or revoke admin access · deactivate departed teammates
        </p>
      </header>

      {isLoading && (
        <p className={styles.placeholder} aria-live="polite">
          Loading…
        </p>
      )}

      {isError && (
        <p className={styles.placeholder} role="alert">
          Couldn&apos;t load users.{' '}
          <button
            type="button"
            className={styles.retry}
            onClick={() => refetch()}
          >
            Retry →
          </button>
        </p>
      )}

      {data && <UserList users={data.users} meId={meId} />}
    </div>
  )
}

function UserList({
  users,
  meId,
}: {
  users: AdminListUser[]
  meId: string | null
}) {
  const sorted = useMemo(() => {
    const list = users.slice()
    list.sort((a, b) => {
      // Deactivated accounts sink to the bottom — they're kept on the
      // page only so the deactivation can be undone.
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1
      if (a.is_admin !== b.is_admin) return a.is_admin ? -1 : 1
      const an = (a.display_name ?? a.slack_user_id).toLowerCase()
      const bn = (b.display_name ?? b.slack_user_id).toLowerCase()
      return an.localeCompare(bn)
    })
    return list
  }, [users])

  if (sorted.length === 0) {
    return <p className={styles.placeholder}>No users.</p>
  }

  return (
    <ul className={styles.list}>
      {sorted.map(user => (
        <UserRow key={user.id} user={user} meId={meId} />
      ))}
    </ul>
  )
}

function UserRow({ user, meId }: { user: AdminListUser; meId: string | null }) {
  const setAdmin = useSetAdmin()
  const [error, setError] = useState<string | null>(null)
  const isSelf = meId !== null && meId === user.id
  const pending = setAdmin.isPending
  const label = user.display_name ?? user.slack_user_id

  const mutate = (payload: AdminUpdatePayload) => {
    setError(null)
    setAdmin.mutate(
      { id: user.id, payload },
      {
        onError: err => setError(messageForError(err)),
      }
    )
  }

  return (
    <li className={`${styles.row} ${user.is_active ? '' : styles.rowInactive}`}>
      <div className={styles.rowMain}>
        <span className={styles.name}>{label}</span>
        <span className={styles.meta}>
          {user.email ?? user.slack_user_id}
          {isSelf ? <span className={styles.selfTag}> · you</span> : null}
        </span>
      </div>
      <div className={styles.rowSide}>
        {user.is_active ? null : (
          <span className={`${styles.badge} ${styles.badgeInactive}`}>
            inactive
          </span>
        )}
        {user.is_admin ? (
          <span className={styles.badge}>admin</span>
        ) : (
          <span className={`${styles.badge} ${styles.badgeMuted}`}>—</span>
        )}
        <button
          type="button"
          className={styles.toggle}
          onClick={() => mutate({ is_admin: !user.is_admin })}
          /* Admin rights on a deactivated account are inert — reactivate first. */
          disabled={pending || !user.is_active}
          aria-label={
            user.is_admin
              ? `Revoke admin from ${label}`
              : `Grant admin to ${label}`
          }
        >
          {user.is_admin ? 'revoke' : 'grant'}
        </button>
        <button
          type="button"
          className={`${styles.toggle} ${styles.toggleQuiet}`}
          onClick={() => mutate({ is_active: !user.is_active })}
          disabled={pending}
          aria-label={
            user.is_active ? `Deactivate ${label}` : `Reactivate ${label}`
          }
        >
          {user.is_active ? 'deactivate' : 'reactivate'}
        </button>
      </div>
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
    </li>
  )
}

// The API already returns human-readable copy for every domain error
// (last_admin, self_revoke, self_deactivate), and it varies by which
// flag was flipped — so the server owns the wording and we just show it.
function messageForError(err: ApiHttpError | Error): string {
  if (err instanceof ApiHttpError) {
    const body = err.body as { error?: { message?: string } }
    if (body?.error?.message) return body.error.message
  }
  return 'Something went wrong. Try again.'
}
